import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Bookmark, TrendingUp, LayoutDashboard, Lock, UserPlus } from 'lucide-react';

const RegisterBenefits = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Don't show if user is already logged in
  if (user) return null;

  const benefits = [
    {
      icon: Bookmark,
      title: t('registerBenefit1Title'),
      description: t('registerBenefit1Desc'),
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      icon: TrendingUp,
      title: t('registerBenefit2Title'),
      description: t('registerBenefit2Desc'),
      color: 'text-blue-500',
      bgColor: 'bg-blue-50',
    },
    {
      icon: LayoutDashboard,
      title: t('registerBenefit3Title'),
      description: t('registerBenefit3Desc'),
      color: 'text-green-500',
      bgColor: 'bg-green-50',
    },
    {
      icon: Lock,
      title: t('registerBenefit4Title'),
      description: t('registerBenefit4Desc'),
      color: 'text-purple-500',
      bgColor: 'bg-purple-50',
    },
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('whyRegister')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('whyRegisterSubtitle')}
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {benefits.map((benefit, index) => (
            <div
              key={benefit.title}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300"
            >
              <div className={`w-12 h-12 ${benefit.bgColor} rounded-lg flex items-center justify-center mb-4`}>
                <benefit.icon className={`w-6 h-6 ${benefit.color}`} />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {benefit.title}
              </h3>
              <p className="text-gray-600 text-sm">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>

        {/* Use Cases Note */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Lock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-blue-800 font-medium">
                {t('useCasesRequireLogin')}
              </p>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <Button
            onClick={() => navigate('/register')}
            size="lg"
            className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
            data-testid="register-benefits-cta"
          >
            <UserPlus className="w-5 h-5 mr-2" />
            {t('createFreeAccount')}
          </Button>
          <p className="text-sm text-gray-500 mt-4">
            {language === 'nl' 
              ? '100% gratis • Geen creditcard nodig • Direct toegang'
              : '100% free • No credit card required • Instant access'}
          </p>
        </div>
      </div>
    </section>
  );
};

export default RegisterBenefits;
